import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Network, BrainCircuit, Bot, ShieldCheck, MessagesSquare, Database, FileUp, Layers } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import OntologyGraph from "@/components/OntologyGraph";
import { ArchitecturePosterV4 } from "@/components/ArchitecturePosterV4";

const CAPABILITIES = [
  {
    icon: Network,
    name: "Ontology",
    img: "/home/news_360.jpg",
    line_en: 'The world model',
    body_en: 'Organizations, people, assets, instruments, deals, transactions, risks, obligations, events, documents, signals, decisions and their relationships. Each object exists only once. Its role depends on context: the same company is the target of a deal, a portfolio position and a supplier of a business.',
    items_en: 'Organizations · People · Assets · Instruments · Deals · Transactions · Risks · Events · Documents · Decisions · Relationships',
    line: "Le modèle du monde",
    body: "Organisations, personnes, actifs, instruments, deals, transactions, risques, obligations, événements, documents, signaux, décisions et leurs relations. Chaque objet existe une seule fois. Son rôle dépend du contexte : la même société est cible d'un deal, position d'un portefeuille et fournisseur d'une entreprise.",
    items: "Organisations · Personnes · Actifs · Instruments · Deals · Transactions · Risques · Événements · Documents · Décisions · Relations",
  },
  {
    icon: BrainCircuit,
    name: "Intelligence",
    img: "/home/news_mirror.jpg",
    line_en: 'Observe, connect, evaluate',
    body_en: 'The signal engine continuously watches registers, news and events. Scoring evaluates, matching brings together, scenarios project, the knowledge graph connects. Each system consumes this intelligence in its own context: origination for Deal, market signals for Markets, regulatory watch for Governance.',
    items_en: 'Radar · Signal Engine · Scoring · Matching · Research · Scenarios · Knowledge Graph · Doctrine',
    line: "Observer, relier, évaluer",
    body: "Le moteur de signaux observe registres, actualités et événements en continu. Le scoring évalue, le matching rapproche, les scénarios projettent, le knowledge graph relie. Chaque système consomme cette intelligence dans son propre contexte : origination pour Deal, signaux de marché pour Markets, veille réglementaire pour Governance.",
    items: "Radar · Signal Engine · Scoring · Matching · Recherche · Scénarios · Knowledge Graph · Doctrine",
  },
  {
    icon: Bot,
    name: "AI & Automation",
    img: "/home/cap_ai.jpg",
    line_en: 'Reason and automate',
    body_en: 'Agents that read, extract, classify, translate, compare and reason. The same engine analyzes a company for an acquisition, a portfolio for a manager or a file for a committee. Same intelligence, different contexts.',
    items_en: 'Agents · Reasoning · Parsing · Extraction · Classification · Translation · Intelligent workflows',
    line: "Raisonner et automatiser",
    body: "Des agents qui lisent, extraient, classifient, traduisent, comparent et raisonnent. Le même moteur analyse une entreprise pour une acquisition, un portefeuille pour un gérant ou un dossier pour un comité. Même intelligence, contextes différents.",
    items: "Agents · Reasoning · Parsing · Extraction · Classification · Traduction · Workflows intelligents",
  },
  {
    icon: ShieldCheck,
    name: "Trust & Security",
    img: "/home/cap_trust.jpg",
    line_en: 'Trust, instrumented',
    body_en: 'Verified identities, role-based permissions, provenance of every piece of data, history, versioning and audit trail. Data hosted in Switzerland, encrypted in transit. A shared ontology has never meant shared rights: each object is visible exactly where it should be.',
    items_en: 'Swiss hosting · Encryption in transit (TLS) · Identities · Permissions & Roles · Provenance · Evidence · Versioning · Audit trail',
    line: "La confiance, outillée",
    body: "Identités vérifiées, permissions par rôle, provenance de chaque donnée, historique, versioning et piste d'audit. Données hébergées en Suisse, chiffrées en transit. Une ontologie commune n'a jamais signifié des droits communs : chaque objet est visible au niveau exact où il doit l'être.",
    items: "Hébergement suisse · Chiffrement en transit (TLS) · Identités · Permissions & Rôles · Provenance · Evidence · Versioning · Audit trail",
  },
  {
    icon: MessagesSquare,
    name: "Communication & Network",
    img: "/home/cap_cercle.jpg",
    line_en: 'The Noam network',
    body_en: "Cercle is Noam's inter-company communication layer: identified people and organizations exchanging around known objects, a deal, an asset, a decision, with permissions, provenance and history. It is not yet another messenger. It is the relational infrastructure of the Noam world.",
    items_en: 'Cercle · Messages · Organization ↔ Organization · Groups & Channels · Notifications · Contextual sharing',
    line: "Le réseau Noam",
    body: "Cercle est la couche de communication inter-entreprises de Noam : des personnes et organisations identifiées qui échangent autour d'objets connus, un deal, un actif, une décision, avec permissions, provenance et historique. Ce n'est pas une messagerie de plus. C'est l'infrastructure relationnelle du monde Noam.",
    items: "Cercle · Messages · Organisation ↔ Organisation · Groupes & Channels · Notifications · Partage contextuel",
    highlight: true,
  },
  {
    icon: Database,
    name: "Data & Infrastructure",
    img: "/home/cap_data.jpg",
    line_en: 'The technical foundation',
    body_en: 'Document drive, object storage, APIs and workflows. Swiss hosting, external data integrated at the source, continuous monitoring.',
    items_en: 'Drive · Object Storage · APIs · Workflows · External data · Monitoring',
    line: "Le socle technique",
    body: "Drive documentaire, object storage, APIs et workflows. Hébergement suisse, données externes intégrées à la source, monitoring continu.",
    items: "Drive · Object Storage · APIs · Workflows · Données externes · Monitoring",
  },
];

const PIPELINE = [
  { label: "Information", sub: "Radar · News · Registres · Données" },
  { label: "Contexte", sub: "Qui ? Quoi ? Où ? Quand ? Pourquoi ?" },
  { label: "Connaissance", sub: "Relations · Exposition · Impact" },
  { label: "Trust", sub: "Conformité · Historique · Réputation" },
  { label: "Opportunité / Risque", sub: "Scénarios · Probabilités" },
  { label: "Décision", sub: "Agir · Contrôler · Investir · Négocier" },
];

const BRIDGES = [
  { from: "Un événement touche une société", deal: "Le deal en cours est re-scoré", markets: "L'exposition du portefeuille est recalculée", gov: "Une alerte fournisseur est levée" },
];


export default function Platform() {
  const { i18n } = useTranslation();
  const en = (i18n.language || "fr").startsWith("en");
  // Registre unique des sources : la même vérité que le backend et l'agent NOAM.
  const [registry, setRegistry] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/source-registry`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setRegistry)
      .catch(() => setRegistry(null));
  }, []);
  useSEO({
    title: en ? "Noam Platform · The shared foundation of operations." : "Noam Platform · Le socle commun des opérations.",
    description:
      "Le socle des trois systèmes Noam : Ontology, Intelligence, AI & Automation, Trust & Security, Communication & Network, Data & Infrastructure. Un même modèle du monde économique, branché sur votre réalité existante.",
    path: "/platform",
  });
  return (
    <div className="relative overflow-hidden" data-testid="platform-root">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img src="/home/platform_hero.jpg" alt="" className="noam-kenburns absolute inset-0 w-full h-full object-cover opacity-35 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/75 to-[#0A0E13]/30 pointer-events-none" />
        <div className="absolute inset-0 noam-vignette pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 py-24 w-full">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500 noam-fade-in">
            <span className="w-8 h-px bg-[#43C6D8]" />
            <span className="text-[#43C6D8]">Noam Platform</span>
            <span className="text-zinc-700">·</span>
            <span>Intelligence & Infrastructure</span>
          </div>
          <h1 className="noam-display text-5xl sm:text-6xl lg:text-[5.5rem] font-bold tracking-tighter leading-[0.95] mt-6 noam-fade-in"
            style={{ animationDelay: "0.15s" }} data-testid="platform-h1">
            {en ? "The shared foundation" : "Le socle commun"}
            <br />
            <span className="text-zinc-500">{en ? "of operations." : "des opérations."}</span>
          </h1>
          <p className="mt-10 text-lg text-zinc-300 max-w-2xl leading-relaxed noam-fade-in" style={{ animationDelay: "0.3s" }}>
            {en ? "Platform is not a fourth piece of software. It is the semantic, intelligent and relational foundation that powers Noam Deal, Noam Financial Markets and Noam Governance. It always starts in the same place: your organization's existing reality." : "Platform n'est pas un quatrième logiciel. C'est le socle sémantique, intelligent et relationnel qui fait fonctionner Noam Deal, Noam Financial Markets et Noam Governance. Il commence toujours au même endroit : la réalité existante de votre organisation."}
          </p>
        </div>
      </section>

      {/* I · POURQUOI TROIS SYSTÈMES NE FONT QU'UN */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{en ? "I · One same world" : "I · Un même monde"}</div>
              <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 leading-tight">
                {en ? "Why three systems" : "Pourquoi trois systèmes"}
                <br />
                {en ? "are one." : "ne font qu'un."}
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6 max-w-md">
                Dans la plupart des organisations, la transaction, l'investissement et
                l'opérationnel vivent dans des logiciels qui s'ignorent. Chez Noam, ils
                partagent le même modèle du monde. Alors l'information circule.
              </p>
            </div>
            <div className="md:col-span-7">
              {BRIDGES.map((b) => (
                <div key={b.from} className="border border-white/8 rounded-md bg-[#0A0E13] p-6" data-testid="platform-bridge">
                  <div className="text-xs text-[#EF9F27] uppercase tracking-[0.15em]">{b.from}</div>
                  <div className="grid sm:grid-cols-3 gap-3 mt-5">
                    <div className="border-l-2 pl-3" style={{ borderColor: "#8E7CF8" }}>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Noam Deal</div>
                      <div className="text-sm text-zinc-200 mt-1">{b.deal}</div>
                    </div>
                    <div className="border-l-2 pl-3" style={{ borderColor: "#378ADD" }}>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Financial Markets</div>
                      <div className="text-sm text-zinc-200 mt-1">{b.markets}</div>
                    </div>
                    <div className="border-l-2 pl-3" style={{ borderColor: "#1D9E75" }}>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Governance</div>
                      <div className="text-sm text-zinc-200 mt-1">{b.gov}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-5">
                    Trois lectures simultanées du même événement, puis une décision traçable qui retourne dans le graphe.
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* II · L'ARCHITECTURE EN UNE IMAGE */}
      <section className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{en ? "II · The complete map" : "II · La carte complète"}</div>
            <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4">{en ? "The architecture in one image." : "L'architecture en une image."}</h2>
            <p className="text-sm text-zinc-400 max-w-xl mx-auto leading-relaxed mt-4">
              Trois Systems, leurs Modules, six Capabilities partagées et la chaîne
              décisionnelle qui les relie. La page raconte le récit. Le poster est la carte.
            </p>
          </div>
          <div className="max-w-5xl mx-auto" data-testid="platform-poster">
            <ArchitecturePosterV4 />
          </div>
        </div>
      </section>

      {/* III · LES SIX CAPACITÉS */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="mb-14">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{en ? "III · The capabilities" : "III · Les capacités"}</div>
            <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4">{en ? "Six families. One foundation." : "Six familles. Un socle."}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {CAPABILITIES.map((c) => (
              <div key={c.name}
                className={`group bg-[#0A0E13] hover:bg-white/[0.02] transition-colors ${c.highlight ? "relative" : ""}`}
                data-testid={`platform-cap-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {c.highlight && <div className="absolute inset-x-0 top-0 h-px bg-[#43C6D8] z-10" />}
                <div className="relative h-40 overflow-hidden border-b border-white/5">
                  <img src={c.img} alt={c.name} className="noam-kenburns w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E13] via-transparent to-transparent" />
                  <div className="noam-scanline" />
                </div>
                <div className="px-7 pb-9 pt-6">
                <div className="flex items-center gap-3">
                  <c.icon className="w-5 h-5 text-[#43C6D8]" />
                  <h3 className="noam-display text-xl font-bold text-white">{c.name}</h3>
                </div>
                <div className="text-xs text-[#43C6D8] uppercase tracking-[0.15em] mt-2">{en ? c.line_en : c.line}</div>
                <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{en ? c.body_en : c.body}</p>
                <div className="text-[11px] text-zinc-600 mt-5 leading-relaxed">{en ? c.items_en : c.items}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IV · ONTOLOGY */}
      <section className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
            <div className="md:col-span-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">IV · Ontology</div>
              <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 leading-tight">
                {en ? "The object before" : "L'objet avant"}
                <br />
                {en ? "the application." : "l'application."}
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6">
                Une entreprise ne devient pas six entreprises parce qu'elle apparaît dans
                six applications. L'objet appartient à l'ontologie. L'application ne fait
                que l'exploiter dans son contexte, avec les permissions, la provenance et
                l'historique qui le suivent partout.
              </p>
            </div>
            <div className="md:col-span-8">
              <OntologyGraph />
            </div>
          </div>
        </div>
      </section>

      {/* V · LA BOUCLE */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{en ? "V · The Noam loop" : "V · La boucle Noam"}</div>
              <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 leading-tight">
                {en ? "From information" : "De l'information"}
                <br />
                {en ? "to the decision." : "à la décision."}
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mt-6 max-w-md">
                La promesse de Platform tient en une phrase : transformer une information
                fragmentée en décision contextualisée et traçable, puis réinjecter cette
                décision dans le graphe pour que la suivante soit plus intelligente.
              </p>
            </div>
            <div className="md:col-span-7 md:pl-8">
              <div className="relative" data-testid="platform-pipeline">
                <div className="absolute left-[13px] top-4 bottom-4 w-px bg-gradient-to-b from-zinc-700 via-[#43C6D8]/40 to-[#1D9E75]" />
                <div className="space-y-3">
                  {PIPELINE.map((p, i) => (
                    <div key={p.label} className="relative flex items-center gap-5 group">
                      <span className={`relative z-10 w-[27px] h-[27px] shrink-0 rounded-full border flex items-center justify-center text-[10px] font-mono ${i === PIPELINE.length - 1 ? "border-[#1D9E75] text-[#1D9E75] bg-[#1D9E75]/10" : "border-white/15 text-zinc-500 bg-[#0A0E13]"}`}>
                        {i + 1}
                      </span>
                      <div className="flex-1 border border-white/5 rounded-md px-5 py-3.5 bg-[#0A0E13] group-hover:border-white/20 transition-colors">
                        <div className={`text-sm font-semibold tracking-wide ${i === PIPELINE.length - 1 ? "text-[#1D9E75]" : "text-zinc-100"}`}>{p.label}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">{p.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 ml-12 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                  ↺ La décision retourne dans le graphe
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VI · SOURCES & ARCHITECTURE OUVERTE */}
      <section className="relative border-t border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-end mb-12">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{en ? "VI · Your sources" : "VI · Vos sources"}</div>
              <h2 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight mt-4 leading-tight">
                {en ? "Plug into what exists," : "Se brancher sur l'existant,"}
                <br />
                {en ? "without disruption." : "sans rupture."}
              </h2>
            </div>
            <div className="md:col-span-7 md:pl-10">
              <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
                {en ? "Noam enriches what exists before asking you to recreate it. Here is what the platform uses today, and how it is designed for what comes next." : "Noam enrichit l'existant avant de demander de le recréer. Voici ce que la plateforme exploite aujourd'hui, et comment elle est conçue pour la suite."}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/5" data-testid="platform-sources">
            {(registry?.available || []).map((s, i) => (
              <div key={s.id} className="bg-[#0A0E13] px-6 py-8 hover:bg-white/[0.02] transition-colors" data-testid={`platform-source-${i}`}>
                <div className="flex items-center gap-2.5">
                  <FileUp className="w-4 h-4 text-[#1D9E75] shrink-0" />
                  <h3 className="noam-display text-base font-bold text-white leading-snug">{s.label}</h3>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#1D9E75] mt-2">{en ? "Available today" : "Disponible aujourd'hui"}</div>
                <p className="text-sm text-zinc-400 mt-4 leading-relaxed">{s.description}</p>
              </div>
            ))}
            {!registry && (
              <div className="bg-[#0A0E13] px-6 py-8 col-span-full text-sm text-zinc-500" data-testid="platform-sources-loading">
                Chargement du registre des sources.
              </div>
            )}
          </div>

          {registry?.preparing?.length > 0 && (
            <div className="mt-4 border border-white/8 rounded-md bg-[#0A0E13] px-6 py-6" data-testid="platform-sources-preparing">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#43C6D8]">{en ? "Connections in preparation" : "Connexions en préparation"}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {registry.preparing.map((s) => (
                  <span key={s.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-300"
                    data-testid={`platform-preparing-${s.id}`}>
                    {s.label}
                    <span className="text-[9px] uppercase tracking-[0.12em] text-zinc-500">{s.category}</span>
                  </span>
                ))}
              </div>
              <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
                {en ? "No date commitment. A connection only appears here when it is genuinely in progress, under the same ingestion contract as the available sources: every piece of data keeps its provenance, nothing is materialized without your validation." : "Aucun engagement de date. Une connexion n'apparaît ici que lorsqu'elle est réellement en chantier, dans le même contrat d'ingestion que les sources disponibles : chaque donnée garde sa provenance, rien n'est matérialisé sans votre validation."}
              </p>
            </div>
          )}

          <div className="mt-4 border border-white/8 rounded-md bg-[#0A0E13] px-6 py-6 flex items-start gap-3" data-testid="platform-sources-honesty">
            <Layers className="w-4 h-4 text-[#43C6D8] shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-zinc-200 font-medium">{en ? "An architecture built to welcome your other sources." : "Une architecture destinée à accueillir vos autres sources."}</div>
              <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed max-w-3xl">
                {en ? "Connections in preparation are not active yet. We prefer to tell you: in the meantime, a file export is enough for Noam to understand, connect and use your data. Every materialization goes through your validation." : "Les connexions en préparation ne sont pas encore actives. Nous préférons vous le dire : en attendant, un export de fichier suffit pour que Noam comprenne, relie et exploite vos données. Chaque matérialisation passe par votre validation."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative border-t border-white/5 bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <Link to="/request-access"
            className="group block bg-[#0A0E13] border border-white/5 px-8 sm:px-12 py-16 sm:py-20 relative overflow-hidden"
            data-testid="platform-cta-request">
            <img src="/home/cta_demo.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-35 group-hover:scale-[1.03] transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E13] via-[#0A0E13]/70 to-transparent" />
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#43C6D8]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">{en ? "Access" : "Accès"}</div>
              <h3 className="noam-display text-3xl sm:text-4xl font-bold tracking-tight text-white group-hover:text-[#43C6D8] transition-colors flex items-center gap-4" data-testid="platform-cta-title">
                {en ? "Instantiate Noam for your organization" : "Instancier Noam pour votre organisation"} <ArrowRight className="w-7 h-7 group-hover:translate-x-1.5 transition-transform" />
              </h3>
              <p className="mt-5 text-sm text-zinc-400 leading-relaxed max-w-xl">
                {en ? "Access is granted on request, after review of your scope. Hosted in Switzerland." : "Les accès sont délivrés sur demande, après examen de votre périmètre. Hébergement en Suisse."}
              </p>
              <div className="mt-8 text-[11px] uppercase tracking-[0.35em] text-zinc-500">
                {en ? "One world. Three systems. One shared intelligence." : "Un monde. Trois systèmes. Une même intelligence."}
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}
